import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ArrowRight, Eye, EyeOff, Check, Sparkles } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import LanguageSwitcher from "@/components/LanguageSwitcher";

export default function AcceptInvitation() {
  const { token } = useParams();
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const { t } = useTranslation();
  const [invite, setInvite] = useState(null); // null=loading, obj=ok, false=error
  const [errorMsg, setErrorMsg] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    api
      .get(`/auth/invitation/${token}`)
      .then((r) => {
        // Payment gate: if this invitation still owes payment, bounce to /pay
        if (r.data.payment_required && !r.data.payment_completed) {
          navigate(`/pay/${token}`, { replace: true });
          return;
        }
        setInvite(r.data);
      })
      .catch((e) => {
        setInvite(false);
        setErrorMsg(formatApiError(e));
      });
  }, [token, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    if (password.length < 8) return toast.error(t("accept.passwordTooShort"));
    if (password !== confirm) return toast.error(t("accept.passwordMismatch"));
    setSubmitting(true);
    try {
      const { data } = await api.post("/auth/accept-invitation", { token, password });
      if (data.token) localStorage.setItem("em_os_token", data.token);
      setUser({ id: data.id, email: data.email, name: data.name, role: data.role });
      toast.success(t("accept.welcomeToast"));
      navigate("/", { replace: true });
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen grid grid-cols-1 lg:grid-cols-2 bg-ink-0 text-white relative z-10">
      {/* Left visual */}
      <div className="hidden lg:block relative overflow-hidden border-r border-white/5">
        <img
          src="https://images.unsplash.com/photo-1778849122862-7fa43a7200f8?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NzB8MHwxfHNlYXJjaHwyfHxhYnN0cmFjdCUyMGRhcmslMjBtaW5pbWFsJTIwdGV4dHVyZSUyMHByZW1pdW18ZW58MHx8fHwxNzg0NTU0NDkwfDA&ixlib=rb-4.1.0&q=85"
          alt=""
          className="absolute inset-0 w-full h-full object-cover opacity-60"
        />
        <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, rgba(10,10,11,0.4) 0%, rgba(10,10,11,0.95) 100%)" }} />
        <div className="relative h-full flex flex-col justify-between p-14">
          <div className="flex items-baseline gap-2">
            <span className="font-display text-3xl font-semibold tracking-tight">EM</span>
            <span className="font-display text-3xl font-light tracking-tight text-gold">Operating System</span>
          </div>
          <div className="max-w-md">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-gold/30 bg-gold/5 mb-6">
              <Sparkles className="w-3 h-3 text-gold" />
              <span className="text-[10px] uppercase tracking-[0.3em] text-gold font-semibold">{t("accept.youAreInvited")}</span>
            </div>
            <h1 className="font-display text-4xl font-light tracking-tight leading-tight text-white">
              {t("accept.welcomeTo")}, {invite ? <span className="text-gold">{invite.name?.split(" ")[0]}</span> : "Leader"}.
            </h1>
            <p className="mt-6 text-zinc-400 text-sm leading-relaxed">
              {t("accept.brandSub")}
            </p>
          </div>
        </div>
      </div>

      {/* Right form */}
      <div className="flex items-center justify-center p-8 relative">
        <div className="absolute top-6 right-6 z-10">
          <LanguageSwitcher />
        </div>
        <div className="w-full max-w-sm">
          <div className="lg:hidden flex items-baseline gap-2 mb-10">
            <span className="font-display text-2xl font-semibold">EM</span>
            <span className="font-display text-2xl font-light text-gold">Operating System</span>
          </div>

          {invite === null && <div className="text-zinc-500 text-sm">{t("accept.loadingInvitation")}</div>}

          {invite === false && (
            <div>
              <p className="text-xs uppercase tracking-[0.3em] text-red-400 mb-3">{t("accept.invitationInvalid")}</p>
              <h2 className="font-display text-3xl font-light tracking-tight mb-4">
                {t("accept.linkExpired")}<span className="text-gold">.</span>
              </h2>
              <p className="text-sm text-zinc-500 leading-relaxed mb-8">{errorMsg}</p>
              <button
                onClick={() => navigate("/login")}
                className="text-sm text-gold hover:text-gold-hover"
              >
                {t("accept.goToSignIn")}
              </button>
            </div>
          )}

          {invite && (
            <>
              <p className="text-xs uppercase tracking-[0.3em] text-gold mb-3">{t("accept.acceptInvitation")}</p>
              <h2 className="font-display text-3xl font-light tracking-tight mb-3">
                {t("accept.setPassword")}<span className="text-gold">.</span>
              </h2>
              <div className="mb-8 flex items-center gap-3 p-3 rounded-lg bg-white/[0.03] border border-white/5">
                <div className="w-9 h-9 rounded-full bg-gold/10 border border-gold/25 flex items-center justify-center text-gold font-semibold text-sm">
                  {(invite.name || "?").charAt(0).toUpperCase()}
                </div>
                <div className="min-w-0">
                  <div className="text-sm text-white truncate">{invite.name}</div>
                  <div className="text-xs text-zinc-500 truncate">{invite.email}</div>
                </div>
                <span className="ml-auto text-[10px] uppercase tracking-widest bg-gold/10 text-gold border border-gold/20 rounded-full px-2 py-0.5">
                  {invite.role}
                </span>
              </div>

              <form onSubmit={submit} className="space-y-5" data-testid="accept-invitation-form">
                <div>
                  <label className="text-xs uppercase tracking-wider text-zinc-500 font-semibold mb-2 block">{t("accept.choosePassword")}</label>
                  <div className="relative">
                    <input
                      data-testid="invite-password"
                      type={showPw ? "text" : "password"}
                      required
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className="em-input pr-11"
                      placeholder={t("accept.passwordMin")}
                      minLength={8}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPw((v) => !v)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-zinc-300"
                      aria-label="Toggle password"
                    >
                      {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>

                <div>
                  <label className="text-xs uppercase tracking-wider text-zinc-500 font-semibold mb-2 block">{t("accept.confirmPassword")}</label>
                  <input
                    data-testid="invite-confirm"
                    type={showPw ? "text" : "password"}
                    required
                    value={confirm}
                    onChange={(e) => setConfirm(e.target.value)}
                    className="em-input"
                    placeholder={t("accept.passwordAgain")}
                    minLength={8}
                  />
                </div>

                <button
                  data-testid="invite-submit"
                  type="submit"
                  disabled={submitting}
                  className="w-full bg-gold text-ink-0 font-semibold rounded-lg px-6 py-3 hover:bg-gold-hover disabled:opacity-50 flex items-center justify-center gap-2 group"
                  style={{ transitionProperty: "background-color", transitionDuration: "180ms" }}
                >
                  <Check className="w-4 h-4" />
                  <span>{submitting ? t("accept.activating") : t("accept.activateAccount")}</span>
                  <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5" style={{ transitionProperty: "transform", transitionDuration: "180ms" }} />
                </button>
              </form>

              <p className="mt-8 text-[11px] text-zinc-600 leading-relaxed">
                {t("accept.confidentialityNotice")}
              </p>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
